import { useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import dynamic from 'next/dynamic';

import clsx from 'clsx';

// Hooks
import useErrorTranslation from '@/utils/hooks/language/useErrorTranslation';
import useUser from '@/utils/hooks/swr/useUser';
import useTranslation from '@/utils/hooks/translation/useTranslation';

// Utils
import { put } from '@/utils/client';
import useIntersectionObserver from '@/utils/hooks/utils/useIntersectionObserver';
import useToggle from '@/utils/hooks/utils/useToggle';

// Libs
import { hasNumber } from '@/lib/regex';
import { PENDO_CLASS_SETTINGS_ACCOUNT_SAVE } from '@/lib/tools/pendo/class';

// Components
import { DefaultAvatar as Avatar } from '@/components/base/Avatar';
import Button from '@/components/base/Button';
import Icon from '@/components/base/Icon';
import InfoBanner from '@/components/base/InfoBanner';
import Input from '@/components/base/Input';
import Label from '@/components/base/Label';
import StickySave from '@/components/composite/Form/StickySave';
import AccountFormEmail from '@/components/pages/settings/AccountFormEmail';

// Endpoints
import { USER_PROFILE_INFOS } from '@/routes/apiEndpoints';
const UpdateProfileAvatarModal = dynamic(
    () => import('@/components/pages/settings/UpdateProfileAvatarModal')
);

// Types
import type { UserType } from '@/types/userType';

export type AccountFormType = {
    first_name: string;
    last_name: UserType['last_name'];
    email: UserType['email'];
};

const AccountForm = () => {
    const { t } = useTranslation('settings');
    const { t_error } = useErrorTranslation();
    const { isOpen, toggle } = useToggle();
    const { user, mutateUser } = useUser();

    // For sticky save
    const refSaveButton = useRef<HTMLButtonElement | null>(null);
    const refBannerInfo = useRef<HTMLDivElement | null>(null);
    const saveInViewPort = useIntersectionObserver(refSaveButton);
    const BannerInfoinViewPort = useIntersectionObserver(refBannerInfo);

    const [formMessages, setFormMessages] = useState<{
        successMessage: string;
        errorMessage: string;
    }>({
        successMessage: null,
        errorMessage: null
    });

    const defaultValues = {
        first_name: user?.first_name,
        last_name: user?.last_name,
        email: user?.email
    };

    const formMethods = useForm({ defaultValues });

    const {
        register,
        setError,
        handleSubmit,
        reset,
        getValues,
        formState: { errors, isSubmitting, isDirty }
    } = formMethods;

    const updateAccountSettings = async (data: AccountFormType) => {
        try {
            const dataUser = {
                first_name: data?.first_name,
                last_name: data?.last_name,
                email: data?.email
            };

            await put(USER_PROFILE_INFOS, dataUser);
            await mutateUser();

            setFormMessages((prevState) => ({
                ...prevState,
                successMessage:
                    user?.email === data?.email
                        ? t('account_page.form_success')
                        : t('account_page.form_success') +
                          ' ' +
                          t('account_page.form_email_changed', { email: data?.email })
            }));
            // Reset default values to reset isDirty
            reset(getValues());
        } catch (e) {
            if (e.status === 422) {
                const errorsFields = e.response?.data?.errors;
                if (errorsFields) {
                    Object.keys(errorsFields).forEach((field: keyof typeof defaultValues) => {
                        const error_type = errorsFields[field][0];
                        // Display error under each field
                        setError(field, {
                            type: 'manual',
                            message: t_error('errors:error_' + field + '_' + error_type)
                        });
                    });
                } else {
                    setFormMessages((prevState) => ({
                        ...prevState,
                        errorMessage: t('errors:error_occured_submit_data')
                    }));
                }
            } else {
                setFormMessages((prevState) => ({
                    ...prevState,
                    errorMessage: t('errors:error_occured_submit_data')
                }));
            }
        }
    };

    const onSubmit = async (data: AccountFormType) => {
        setFormMessages({
            errorMessage: null,
            successMessage: null
        });
        await updateAccountSettings(data);

        // Scroll To info banner if it's not visible in the viewport
        if (refBannerInfo?.current && !BannerInfoinViewPort) {
            refBannerInfo.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)} id="settings-platform-form">
                <div ref={refBannerInfo} />
                {formMessages.successMessage !== null && (
                    <InfoBanner
                        type="success"
                        title={t('commons:success')}
                        message={formMessages.successMessage}
                        className="mb-8 w-full"
                        data-testid="banner-success"
                        onClose={() =>
                            setFormMessages((prevState) => ({
                                ...prevState,
                                successMessage: null
                            }))
                        }
                    />
                )}
                {formMessages.errorMessage !== null && (
                    <InfoBanner
                        title={t('commons:error')}
                        message={formMessages.errorMessage}
                        className="mb-8 w-full"
                        data-testid="banner-error"
                        onClose={() =>
                            setFormMessages((prevState) => ({
                                ...prevState,
                                errorMessage: null
                            }))
                        }
                    />
                )}
                <div className="mb-8 flex flex-col items-center space-y-2 rounded-md bg-icoGray-100 p-5 dark:bg-icoDarkMode-deepBlack sm:flex-row sm:space-x-5 sm:space-y-0">
                    <div>
                        <button
                            type="button"
                            className="relative inline-block cursor-pointer"
                            data-testid="open-update-avatar-modal-btn"
                            onClick={toggle}>
                            <Avatar
                                size="xl"
                                radius="rounded-md"
                                url={user?.settings?.avatar ?? null}
                                className="dark:bg-icoDarkMode-anthracite"
                                fallback={
                                    user?.first_name && user?.last_name
                                        ? user.first_name.charAt(0) + user.last_name.charAt(0)
                                        : ''
                                }
                            />
                            <span
                                className="absolute -right-2 -top-2 z-1 flex h-6 w-6 min-w-min items-center justify-center rounded-full bg-white p-0 dark:bg-icoDarkMode-anthracite"
                                title={t(
                                    'settings:account_page.update_profile_picture_modal.update_your_picture'
                                )}>
                                <Icon
                                    icon="edit"
                                    className="text-xs text-icoBlue dark:text-white"
                                />
                            </span>
                        </button>
                    </div>

                    <div className="flex flex-1 flex-col space-y-2 text-center sm:text-left">
                        <p className="text-9 uppercase tracking-widest text-icoBlue">
                            {t('account_page.your_profile')}
                        </p>
                        <p className="text-xl">
                            {user?.first_name} {user?.last_name}
                        </p>
                        <p className="text-icoGray-500 dark:text-icoDarkMode-wolf">{user?.email}</p>
                    </div>
                </div>
                <div className="flex flex-col space-y-m">
                    <div className="flex flex-col space-y-m lg:flex-row lg:space-x-5 lg:space-y-0">
                        <div className="w-full">
                            <Label required htmlFor="first_name">
                                {t('account_page.firstname')}
                            </Label>
                            <Input
                                id="first_name"
                                type="text"
                                name="first_name"
                                placeholder={t('account_page.enter_your_first_name')}
                                error={errors?.first_name?.message}
                                {...register('first_name', {
                                    required: t('errors:error_first_name_required'),
                                    validate: {
                                        number: (value) =>
                                            !hasNumber.test(value) ||
                                            t('errors:field_without_number')
                                    }
                                })}
                            />
                        </div>
                        <div className="w-full">
                            <Label required htmlFor="last_name">
                                {t('account_page.lastname')}
                            </Label>
                            <Input
                                id="last_name"
                                type="text"
                                name="last_name"
                                placeholder={t('account_page.enter_your_last_name')}
                                error={errors?.last_name?.message}
                                {...register('last_name', {
                                    required: t('errors:error_last_name_required'),
                                    validate: {
                                        number: (value) =>
                                            !hasNumber.test(value) ||
                                            t('errors:field_without_number')
                                    }
                                })}
                            />
                        </div>
                    </div>
                    <AccountFormEmail
                        setError={(message) =>
                            setFormMessages((prevState) => ({
                                ...prevState,
                                errorMessage: message
                            }))
                        }
                        updateEmail={(email) => {
                            reset((formValues) => ({
                                ...formValues,
                                email
                            }));
                            setFormMessages((prevState) => ({
                                ...prevState,
                                successMessage: t('account_page.email_changed_success')
                            }));
                        }}
                    />
                </div>
                <div className="mt-8 flex justify-end">
                    <Button
                        disabled={!isDirty}
                        ref={refSaveButton}
                        type="submit"
                        data-testid="submitButton"
                        isLoading={isSubmitting}
                        className={PENDO_CLASS_SETTINGS_ACCOUNT_SAVE}>
                        {t('save')}
                    </Button>
                </div>
            </form>
            <div className="-mx-8">
                <StickySave isVisible={!saveInViewPort && isDirty} className="xl:!px-8">
                    <Button
                        isLoading={isSubmitting}
                        size="sm"
                        className={clsx('rounded-m', PENDO_CLASS_SETTINGS_ACCOUNT_SAVE)}
                        onClick={handleSubmit(onSubmit)}>
                        <p className="text-xs">{t('save')}</p>
                    </Button>
                </StickySave>
            </div>
            <UpdateProfileAvatarModal
                isOpen={isOpen}
                close={toggle}
                setSuccessMessage={(message: string) => {
                    setFormMessages((prevState) => ({
                        ...prevState,
                        successMessage: message
                    }));
                }}
                user={user}
                mutateUser={mutateUser}
            />
        </FormProvider>
    );
};

export default AccountForm;
